import type { Effect, Reducer, Subscription } from '@umijs/max';
import type { SingleGoodType } from './data';
import { addGood, editGood, getGoodPage, removeGood, exportGood, importGood } from './service';

export type GoodState = {
  code: number;
  type: string;
  message: string;
  total: number;
  current: number;
  pageSize: number;
  items: SingleGoodType[];
};

type GoodModelType = {
  namespace: string;
  state: GoodState;
  reducers: {
    GoodReducer: Reducer<GoodState>;
  };
  effects: {
    fetchGoodPageEffect: Effect;
    refreshDataEffect: Effect;
    addGoodEffect: Effect;
    editGoodEffect: Effect;
    removeGoodEffect: Effect;
    exportGoodEffect: Effect;
    importGoodEffect: Effect;
  };
  subscriptions: {
    setup: Subscription;
  };
};

// 通用操作处理函数
const createEffect = (serviceCall: any) =>
  function* (
    { payload: action, callback }: any,
    { call, put }: any,
  ): Generator<any, void, any> {
    try {
      const res: any = yield call(
        serviceCall,
        action.values || { id: action.id },
      );

      if (res && res.code === 0) {
        yield put({
          type: 'refreshDataEffect',
          payload: { ...action },
        });
      }

      callback?.(res);
    } catch (e) {}
  };

const GoodModel: GoodModelType = {
  namespace: 'good',
  state: {
    code: 0,
    type: '',
    message: 'success',
    items: [],
    total: 0,
    pageSize: 20,
    current: 1,
  },

  reducers: {
    GoodReducer(state, action) {
      const payload = action.payload || {};
      const items = payload.data?.items;
      return {
        ...state,
        ...payload,
        items: Array.isArray(items) ? items : [],
        total: payload.data?.total || 0,
        current: payload.data?.current || 1,
        pageSize: payload.data?.pageSize || 20,
      } as GoodState;
    },
  },

  effects: {
    *fetchGoodPageEffect(
      { payload: { pageSize, current, filter, sorter, with: withParam } }: any,
      { put, call }: any,
    ): Generator<any, void, any> {
      try {
        const res: any = yield call(getGoodPage, {
          pageSize,
          current,
          filter,
          sorter,
          with: withParam,
        });
        yield put({
          type: 'GoodReducer',
          payload: res,
        });
      } catch (e) {
        console.error('Fetch data failed:', e);
      }
    },

    // 通用刷新数据的 effect
    *refreshDataEffect(
      { payload: action }: any,
      { put, select }: any,
    ): Generator<any, void, any> {
      const current = yield select((state: any) => state.good.current);
      const pageSize = yield select((state: any) => state.good.pageSize);
      yield put({
        type: 'fetchGoodPageEffect',
        payload: {
          current,
          pageSize,
          filter: { ...action.filter },
          sorter: action.sorter || [],
          with: [
            'shop',
            'brand',
            'category.parent.parent',
            'imageList',
            'attrList',
            'optionList',
          ],
        },
      });
    },

    // 简化的 effects
    addGoodEffect: createEffect(addGood),
    editGoodEffect: createEffect(editGood),
    removeGoodEffect: createEffect(removeGood),

    // 导出商品
    *exportGoodEffect(
      { payload, callback }: any,
      { call }: any,
    ): Generator<any, void, any> {
      try {
        const res: any = yield call(exportGood, payload);
        callback?.(res);
      } catch (e) {
        console.error('Export failed:', e);
      }
    },

    // 导入商品
    *importGoodEffect(
      { payload, callback }: any,
      { call, put }: any,
    ): Generator<any, void, any> {
      try {
        const res: any = yield call(importGood, payload);
        if (res && res.code === 0) {
          yield put({
            type: 'refreshDataEffect',
            payload: {},
          });
        }
        callback?.(res);
      } catch (e) {
        console.error('Import failed:', e);
      }
    },
  },

  subscriptions: {
    setup() {},
  },
};

export default GoodModel;
